import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MODULES_CATALOG } from '../../data/modulesCatalog';
import { clearModuleResponseLocalStorage, resetUserModuleProgress } from '../../services/lmsData';
import { auth } from '../../firebase';
import { paintCertificateDynamicText, CERTIFICATE_TEMPLATE_PATH } from '../../utils/certificateCanvas';
import { validatePassword } from '../../utils/passwordPolicy';
import { resolveParticipantDisplayName } from '../../utils/participantDisplayName';
import './Profile.css';

/** All modules use the same template art until you add variants in `certificateCanvas.js`. */
const DEFAULT_CERTIFICATE_TEMPLATE_PATH = CERTIFICATE_TEMPLATE_PATH;
const CERTIFICATE_TEMPLATE_BY_MODULE_ID = {
  'workplace-module': CERTIFICATE_TEMPLATE_PATH,
  'intro-module': CERTIFICATE_TEMPLATE_PATH,
  'educator-module': CERTIFICATE_TEMPLATE_PATH,
  'community-module': CERTIFICATE_TEMPLATE_PATH,
  'parent-module': CERTIFICATE_TEMPLATE_PATH,
};

const Profile = ({ user }) => {
  const location = useLocation();
  const { moduleProgress, firebaseUser, refreshModuleProgress, useFirebase, updateUserDisplayName, changeUserPassword } =
    useAuth();
  const [resettingProgress, setResettingProgress] = useState(false);
  const [downloadingCertificateId, setDownloadingCertificateId] = useState('');
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editCurrentPassword, setEditCurrentPassword] = useState('');
  const [editNewPassword, setEditNewPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editError, setEditError] = useState('');
  const [submissionCounts, setSubmissionCounts] = useState({
    reflection: 0,
    workshop: 0,
    meta: 0,
  });

  const moduleById = useMemo(
    () => Object.fromEntries(MODULES_CATALOG.map((m) => [m.id, m])),
    []
  );

  /** Map moduleId → progress doc (Firestore / local). */
  const progressByModuleId = useMemo(
    () => Object.fromEntries((moduleProgress || []).map((p) => [p.moduleId, p])),
    [moduleProgress]
  );

  /**
   * Average completion across catalog modules that are live (not “coming soon”).
   * Each module contributes 0-100 from progressPercent, or 100 when completed.
   */
  const overallLearningPercent = useMemo(() => {
    const available = MODULES_CATALOG.filter((m) => !m.comingSoon);
    if (!available.length) return 0;
    let sum = 0;
    for (const m of available) {
      const p = progressByModuleId[m.id];
      if (!p) {
        sum += 0;
        continue;
      }
      if (p.status === 'completed' || p.isCompleted) {
        sum += 100;
        continue;
      }
      sum += Math.min(100, Math.max(0, Number(p.progressPercent) || 0));
    }
    return Math.round(sum / available.length);
  }, [progressByModuleId]);

  /** Animated ring value (0 → overallLearningPercent) for CSS transition. */
  const [learningRingPct, setLearningRingPct] = useState(0);
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setLearningRingPct(overallLearningPercent);
      return;
    }
    setLearningRingPct(0);
    const id = window.requestAnimationFrame(() => {
      setLearningRingPct(overallLearningPercent);
    });
    return () => window.cancelAnimationFrame(id);
  }, [overallLearningPercent]);

  useEffect(() => {
    try {
      const reflection = JSON.parse(localStorage.getItem('lms-workplace-reflection-submissions') || '[]').length;
      const workshop = JSON.parse(localStorage.getItem('lms-workplace-workshop-enquiries') || '[]').length;
      const meta = JSON.parse(localStorage.getItem('lms-meta-reflexivity-download-responses') || '[]').length;
      setSubmissionCounts({ reflection, workshop, meta });
    } catch {
      setSubmissionCounts({ reflection: 0, workshop: 0, meta: 0 });
    }
  }, []);

  useEffect(() => {
    if (location.pathname !== '/profile') return;
    const rawHash = (location.hash || '').replace(/^#/, '');
    if (rawHash !== 'certificates') return;
    const t = window.setTimeout(() => {
      document.getElementById('certificates')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  const formatTimestamp = (value) => {
    if (!value) return '-';
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const inProgressModules = useMemo(
    () => (moduleProgress || []).filter((m) => m.status === 'in-progress'),
    [moduleProgress]
  );

  const completedModules = useMemo(
    () => (moduleProgress || []).filter((m) => m.status === 'completed' || m.isCompleted),
    [moduleProgress]
  );

  const achievements = useMemo(() => {
    const list = [];
    if (completedModules.length > 0) {
      list.push({
        id: 'first-complete',
        title: 'First Module Completed',
        icon: '🎯',
        date: formatTimestamp(completedModules[0]?.completedAt),
      });
    }
    if (submissionCounts.reflection > 0) {
      list.push({
        id: 'reflection-shared',
        title: 'Shared Reflection',
        icon: '💭',
        date: 'From your activity',
      });
    }
    if (submissionCounts.meta > 0) {
      list.push({
        id: 'meta-reflective',
        title: 'Meta-Reflexivity Practiced',
        icon: '🧠',
        date: 'From your activity',
      });
    }
    return list;
  }, [completedModules, submissionCounts.reflection, submissionCounts.meta]);

  const certificates = useMemo(
    () =>
      completedModules.map((item) => ({
        id: item.moduleId,
        title: moduleById[item.moduleId]?.title || item.moduleId,
        date: formatTimestamp(item.completedAt || item.updatedAt),
        module: moduleById[item.moduleId]?.title || item.moduleId,
      })),
    [completedModules, moduleById]
  );

  const activityHistory = useMemo(() => {
    const activities = [];
    (moduleProgress || []).forEach((item) => {
      const moduleTitle = moduleById[item.moduleId]?.title || item.moduleId;
      if (item.enrolledAt) {
        activities.push({
          id: `${item.moduleId}-enrolled`,
          action: 'Enrolled in module',
          title: moduleTitle,
          when: item.enrolledAt,
        });
      }
      if (item.currentStageId) {
        activities.push({
          id: `${item.moduleId}-stage`,
          action: 'Reached stage',
          title: `${moduleTitle} • ${String(item.currentStageId).replace(/-/g, ' ')}`,
          when: item.lastVisitedAt || item.updatedAt,
        });
      }
      if (item.completedAt || item.status === 'completed') {
        activities.push({
          id: `${item.moduleId}-complete`,
          action: 'Completed module',
          title: moduleTitle,
          when: item.completedAt || item.updatedAt,
        });
      }
    });

    if (submissionCounts.reflection > 0) {
      activities.push({
        id: 'reflection-submit',
        action: 'Submitted reflection',
        title: 'Pause & Reflect (Building Workplace Bridges)',
        when: null,
      });
    }
    if (submissionCounts.workshop > 0) {
      activities.push({
        id: 'workshop-submit',
        action: 'Submitted workshop enquiry',
        title: 'Building Workplace Bridges',
        when: null,
      });
    }

    return activities
      .sort((a, b) => {
        const ad = a.when?.toDate ? a.when.toDate() : a.when ? new Date(a.when) : new Date(0);
        const bd = b.when?.toDate ? b.when.toDate() : b.when ? new Date(b.when) : new Date(0);
        return bd - ad;
      })
      .slice(0, 8)
      .map((item) => ({
        ...item,
        date: item.when ? formatTimestamp(item.when) : 'From local activity',
      }));
  }, [moduleProgress, moduleById, submissionCounts.reflection, submissionCounts.workshop]);

  const accountUsesEmailPassword = useCallback(() => {
    const u = auth?.currentUser;
    return Boolean(u?.providerData?.some((p) => p.providerId === 'password'));
  }, []);

  const openEditProfile = () => {
    setEditMessage('');
    setEditError('');
    setEditCurrentPassword('');
    setEditNewPassword('');
    setEditConfirmPassword('');
    const initialName = useFirebase
      ? String(firebaseUser?.displayNameRaw ?? firebaseUser?.displayName ?? '').trim()
      : String(user?.name || '').trim();
    setEditDisplayName(initialName);
    setEditProfileOpen(true);
  };

  const closeEditProfile = () => {
    if (editSaving) return;
    setEditProfileOpen(false);
  };

  const handleSaveProfile = async () => {
    setEditMessage('');
    setEditError('');
    const nameTrim = String(editDisplayName || '').trim();
    if (!nameTrim) {
      setEditError('Please enter your name (used on certificates).');
      return;
    }
    const wantPasswordChange = Boolean(String(editNewPassword || '').trim() || String(editConfirmPassword || '').trim());
    if (wantPasswordChange && !useFirebase) {
      setEditError('Password change is not available in demo mode.');
      return;
    }
    if (wantPasswordChange) {
      const pwCheck = validatePassword(editNewPassword);
      if (!pwCheck.ok) {
        setEditError(pwCheck.message);
        return;
      }
      if (editNewPassword !== editConfirmPassword) {
        setEditError('New password and confirmation do not match.');
        return;
      }
      if (!String(editCurrentPassword || '').trim()) {
        setEditError('Enter your current password to set a new one.');
        return;
      }
    }
    setEditSaving(true);
    try {
      const nameResult = await updateUserDisplayName(nameTrim);
      if (!nameResult.success) {
        setEditError(nameResult.error || 'Could not update display name.');
        return;
      }
      if (wantPasswordChange && useFirebase) {
        if (!accountUsesEmailPassword()) {
          setEditError('This account cannot change password here.');
          return;
        }
        const pwResult = await changeUserPassword(editCurrentPassword, editNewPassword);
        if (!pwResult.success) {
          setEditError(pwResult.error || 'Could not update password.');
          return;
        }
      }
      setEditMessage('Profile updated.');
      setEditCurrentPassword('');
      setEditNewPassword('');
      setEditConfirmPassword('');
      setTimeout(() => {
        setEditProfileOpen(false);
        setEditMessage('');
      }, 900);
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetProgress = async () => {
    if (!firebaseUser?.uid || resettingProgress) return;
    const confirmed = window.confirm(
      'Reset all module progress and submitted responses for this account? This removes Firestore progress, surveys, and other module submissions linked to you.'
    );
    if (!confirmed) return;

    setResettingProgress(true);
    try {
      await resetUserModuleProgress(firebaseUser.uid);
      clearModuleResponseLocalStorage();
      setSubmissionCounts({ reflection: 0, workshop: 0, meta: 0 });

      await refreshModuleProgress?.();
    } finally {
      setResettingProgress(false);
    }
  };

  const handleDownloadCertificate = async (certificate) => {
    if (!certificate?.id || downloadingCertificateId) return;
    setDownloadingCertificateId(certificate.id);
    try {
      const baseUrl = process.env.PUBLIC_URL || '';
      const templatePath =
        CERTIFICATE_TEMPLATE_BY_MODULE_ID[certificate.id] || DEFAULT_CERTIFICATE_TEMPLATE_PATH;
      const templateUrl = `${baseUrl}/${templatePath}`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = templateUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const email = String(firebaseUser?.email || user?.email || '').trim();
      const participantName = resolveParticipantDisplayName({
        displayNameRaw: firebaseUser?.displayNameRaw,
        displayName: firebaseUser?.displayName ?? user?.name,
        email,
        fallbackLabel: 'Participant',
      });
      const moduleDisplayName = certificate.title || moduleById[certificate.id]?.title || certificate.id;

      paintCertificateDynamicText(ctx, canvas, { participantName, moduleDisplayName });

      const safeName = participantName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'participant';
      const safeModule = String(certificate.title || 'certificate')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'module';

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${safeModule}-certificate-${safeName}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.warn('[profile] certificate download fallback', err);
      const fallbackPath =
        CERTIFICATE_TEMPLATE_BY_MODULE_ID[certificate.id] || DEFAULT_CERTIFICATE_TEMPLATE_PATH;
      const fallback = document.createElement('a');
      fallback.href = `${process.env.PUBLIC_URL || ''}/${fallbackPath}`;
      fallback.download = 'certificate.png';
      document.body.appendChild(fallback);
      fallback.click();
      fallback.remove();
    } finally {
      setDownloadingCertificateId('');
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-banner"></div>
          <div className="profile-info">
            <div className="profile-avatar-container">
              <div className="profile-avatar">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} />
                ) : (
                  <span>{(user.name || 'U').split(/\s+/).filter(Boolean).map((n) => n[0]).join('')}</span>
                )}
              </div>
              <button className="edit-avatar-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </button>
            </div>
            <div className="profile-details">
              <h1 className="profile-name">{user.name}</h1>
              {/* <p className="profile-role">{user.role}</p> */}
              <p className="profile-email">{user.email}</p>
            </div>
            <button type="button" className="edit-profile-btn" onClick={openEditProfile}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Edit Profile
            </button>
            <button
              type="button"
              className="reset-progress-btn"
              onClick={handleResetProgress}
              disabled={resettingProgress || !firebaseUser?.uid}
              title="Reset this user's module progress"
            >
              {resettingProgress ? 'Resetting...' : 'Reset My Progress'}
            </button>
          </div>
        </div>

        <div className="profile-stats">
          <div className="stat-box">
            <div className="stat-icon purple">📚</div>
            <div className="stat-content">
              <span className="stat-value">{user.enrolledModules}</span>
              <span className="stat-label">Enrolled Modules</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-icon orange">✨</div>
            <div className="stat-content">
              <span className="stat-value">{user.completedModules}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-icon teal">🏆</div>
            <div className="stat-content">
              <span className="stat-value">{inProgressModules.length}</span>
              <span className="stat-label">In Progress</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-icon pink">📜</div>
            <div className="stat-content">
              <span className="stat-value">{moduleProgress?.length || 0}</span>
              <span className="stat-label">Tracked Modules</span>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-main">
            <section className="profile-section">
              <h2 className="section-title">Current Module Progress</h2>
              {inProgressModules.length > 0 ? (
                <div className="certificates-list">
                  {inProgressModules.map((item) => (
                    <div key={item.moduleId} className="certificate-card">
                      <div className="certificate-icon">{moduleById[item.moduleId]?.icon || '📘'}</div>
                      <div className="certificate-info">
                        <h3 className="certificate-title">{moduleById[item.moduleId]?.title || item.moduleId}</h3>
                        <p className="certificate-meta">
                          {Math.round(Number(item.progressPercent || 0))}% complete • Stage: {String(item.currentStageId || 'not started').replace(/-/g, ' ')}
                        </p>
                      </div>
                      <span className="profile-badge">In Progress</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="profile-empty">No module is currently in progress.</p>
              )}
            </section>

            <section className="profile-section">
              <h2 className="section-title">Achievements</h2>
              <div className="achievements-grid">
                {achievements.length > 0 ? (
                  achievements.map(achievement => (
                    <div key={achievement.id} className="achievement-card">
                      <div className="achievement-icon">{achievement.icon}</div>
                      <h3 className="achievement-title">{achievement.title}</h3>
                      <p className="achievement-date">{achievement.date}</p>
                    </div>
                  ))
                ) : (
                  <p className="profile-empty">Complete modules and submit reflections to unlock achievements.</p>
                )}
              </div>
            </section>

            <section className="profile-section" id="certificates" aria-labelledby="profile-certificates-heading">
              <h2 id="profile-certificates-heading" className="section-title">
                Certificates
              </h2>
              <div className="certificates-list">
                {certificates.length > 0 ? (
                  certificates.map((certificate) => (
                    <div key={certificate.id} className="certificate-card">
                      <div className="certificate-icon" aria-hidden="true">
                        {moduleById[certificate.id]?.icon || '🎓'}
                      </div>
                      <div className="certificate-info">
                        <h3 className="certificate-title">{certificate.title}</h3>
                        <p className="certificate-meta">{certificate.module} • {certificate.date}</p>
                      </div>
                      <button
                        type="button"
                        className="certificate-btn"
                        onClick={() => void handleDownloadCertificate(certificate)}
                        disabled={downloadingCertificateId === certificate.id}
                      >
                        {downloadingCertificateId === certificate.id ? 'Preparing...' : 'Download'}
                      </button>
                      <span className="profile-badge profile-badge--completed">Completed</span>
                    </div>
                  ))
                ) : (
                  <p className="profile-empty">Complete a module to see certificates here.</p>
                )}
              </div>
            </section>

            <section className="profile-section">
              <h2 className="section-title">Activity History</h2>
              <div className="activity-timeline">
                {activityHistory.length > 0 ? (
                  activityHistory.map(activity => (
                    <div key={activity.id} className="timeline-item">
                      <div className="timeline-marker"></div>
                      <div className="timeline-content">
                        <span className="activity-action">{activity.action}</span>
                        <span className="activity-title">{activity.title}</span>
                        <span className="activity-date">{activity.date}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="profile-empty">Your activity will appear here as you continue learning.</p>
                )}
              </div>
            </section>
          </div>

          <aside className="profile-sidebar">
            <div className="sidebar-widget">
              <h3 className="widget-title">Learning Progress</h3>
              <div className="progress-circle">
                <svg viewBox="0 0 36 36" className="circular-chart">
                  <path className="circle-bg"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="circle"
                    strokeDasharray={`${learningRingPct}, 100`}
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <text x="18" y="20.35" className="percentage">{overallLearningPercent}%</text>
                </svg>
              </div>
              <p className="progress-text">Overall course completion</p>
            </div>

            <div className="sidebar-widget">
              <h3 className="widget-title">Learning Streak</h3>
              <div className="streak-info">
                <div className="streak-number">{moduleProgress?.length || 0}</div>
                <div className="streak-text">modules tracked 🔥</div>
              </div>
            </div>

            {/* <div className="sidebar-widget">
              <h3 className="widget-title">Quick Links</h3>
              <div className="quick-links">
                <a href="#settings" className="quick-link">Account Settings</a>
                <a href="#privacy" className="quick-link">Privacy Settings</a>
                <a href="#notifications" className="quick-link">Notifications</a>
              </div>
            </div> */}
          </aside>
        </div>
      </div>

      {editProfileOpen && (
        <div className="profile-edit-overlay" role="presentation" onClick={closeEditProfile}>
          <div
            className="profile-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="profile-edit-title" className="profile-edit-title">
              Edit profile
            </h2>
            <p className="profile-edit-lead">
              Your name appears in the header and on downloaded certificates. Use the same name you registered with, or update it here if needed.
            </p>
            <label className="profile-edit-label" htmlFor="profile-edit-display-name">
              Display name
            </label>
            <input
              id="profile-edit-display-name"
              type="text"
              className="profile-edit-input"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              autoComplete="name"
              maxLength={80}
            />
            {useFirebase && accountUsesEmailPassword() ? (
              <>
                <h3 className="profile-edit-subtitle">Change password</h3>
                <p className="profile-edit-hint">Leave new password blank to keep your current password.</p>
                <label className="profile-edit-label" htmlFor="profile-edit-current-pw">
                  Current password
                </label>
                <input
                  id="profile-edit-current-pw"
                  type="password"
                  className="profile-edit-input"
                  value={editCurrentPassword}
                  onChange={(e) => setEditCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <label className="profile-edit-label" htmlFor="profile-edit-new-pw">
                  New password
                </label>
                <input
                  id="profile-edit-new-pw"
                  type="password"
                  className="profile-edit-input"
                  value={editNewPassword}
                  onChange={(e) => setEditNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <label className="profile-edit-label" htmlFor="profile-edit-confirm-pw">
                  Confirm new password
                </label>
                <input
                  id="profile-edit-confirm-pw"
                  type="password"
                  className="profile-edit-input"
                  value={editConfirmPassword}
                  onChange={(e) => setEditConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </>
            ) : useFirebase ? (
              <p className="profile-edit-hint">
                Password cannot be changed here for this sign-in method. Use your provider’s account settings, or sign out and use Forgot password on the login page if email login is linked.
              </p>
            ) : (
              <p className="profile-edit-hint">Demo mode: display name updates for this session only.</p>
            )}
            {editError ? (
              <p className="profile-edit-error" role="alert">
                {editError}
              </p>
            ) : null}
            {editMessage ? (
              <p className="profile-edit-success" role="status">
                {editMessage}
              </p>
            ) : null}
            <div className="profile-edit-actions">
              <button type="button" className="profile-edit-btn profile-edit-btn--ghost" onClick={closeEditProfile} disabled={editSaving}>
                Cancel
              </button>
              <button type="button" className="profile-edit-btn profile-edit-btn--primary" onClick={() => void handleSaveProfile()} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

